import fs from 'node:fs'
import path from 'node:path'
import type { Schema } from '@cli-types/core-engine/schema.js'
import type {
  CoreSchemasScaffoldOptions,
  CoreSchemasScaffoldResult
} from '@cli-types/next-adapter/init.js'
import { BARREL_SCOPE } from '@cli-constants/snapshots.js'
import { getCurrentCliVersion } from '@cli-utils/paths.js'
import { ensureSnapshotGitFiles } from '@core-engine/snapshots/gitignore/ensure-snapshot-git-files.js'
import { writeProjectFile } from '@core-engine/snapshots/merge/write-project-file.js'
import { createManifest } from '@core-engine/snapshots/store/create-manifest.js'
import { saveManifest } from '@core-engine/snapshots/store/save-manifest.js'
import { writeSnapshotFiles } from '@core-engine/snapshots/store/write-snapshot-files.js'
import { runSinglePipeline } from '@next-adapter/generators/run-single-pipeline.js'
import { defaultSettingsSchema } from '@next-adapter/init/templates/core/default-settings.js'
import { ensureCoreSchemaOwnership } from '@next-adapter/preset-runtime/ensure-core-schema-ownership.js'
import { renderBarrelFiles } from '@next-adapter/snapshots/barrels/render-barrel-files.js'
import { getGeneratedFilePaths } from './get-generated-file-paths.js'

export function scaffoldCoreSchemas({
  cwd,
  config
}: CoreSchemasScaffoldOptions): CoreSchemasScaffoldResult {
  const result: CoreSchemasScaffoldResult = {
    schemas: [],
    generatedFiles: [],
    errors: []
  }

  const schemaContent = defaultSettingsSchema()
  const schemaName = 'settings'
  const schemaPath = path.join(cwd, config.paths.schemas, `${schemaName}.json`)
  fs.mkdirSync(path.dirname(schemaPath), { recursive: true })
  fs.writeFileSync(schemaPath, schemaContent, 'utf-8')
  result.schemas.push(`${schemaName}.json`)

  ensureSnapshotGitFiles(cwd)

  try {
    const parsed = JSON.parse(schemaContent) as Schema
    const cliVersion = getCurrentCliVersion()
    const pipelineResult = runSinglePipeline(parsed, cwd, config, {
      force: true,
      silent: true,
      mode: 'init'
    })

    for (const file of pipelineResult.files) {
      writeProjectFile(cwd, file)
    }

    writeSnapshotFiles(cwd, parsed.name, pipelineResult.files)
    saveManifest(
      cwd,
      parsed.name,
      createManifest(parsed.name, parsed, cliVersion, pipelineResult.files, [])
    )
    ensureCoreSchemaOwnership(cwd, parsed.name)
    result.generatedFiles.push(...getGeneratedFilePaths(pipelineResult.files))

    if (!pipelineResult.success) {
      result.errors.push(...pipelineResult.errors.map((error) => `${schemaName}.json: ${error}`))
    }

    const barrelFiles = renderBarrelFiles(cwd, config)
    for (const file of barrelFiles) {
      writeProjectFile(cwd, file)
    }
    writeSnapshotFiles(cwd, BARREL_SCOPE, barrelFiles)
    saveManifest(
      cwd,
      BARREL_SCOPE,
      createManifest(BARREL_SCOPE, { name: BARREL_SCOPE }, cliVersion, barrelFiles, [])
    )
    result.generatedFiles.push(...getGeneratedFilePaths(barrelFiles))
  } catch (error) {
    result.errors.push(
      `${schemaName}.json: ${error instanceof Error ? error.message : String(error)}`
    )
  }

  return result
}
